<script lang="ts">
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getNumBackInUnits, sanitizeInputs, toBN } from '$lib/util/util';
	import { quadOut } from 'svelte/easing';
	import { fade } from 'svelte/transition';
	import { executionType, limitOrderExpiry, marginInputForTrade, priceImpactDelta } from './store';
	import { swapSlippage, tokensAllowedForDepositBalance } from '$lib/home/store';
	import { AUTOSWAP_STABLES } from '$lib/settings';
	import { findRequiredCallData } from '$lib/data/matchaCallData';
	import BigNumber from 'bignumber.js';
	import { onDestroy } from 'svelte';

	export let showMenu = false;
	let limitOrderInterval = '30d';
	const closeMenu = () => {
		showMenu = false;
	};

	const intervals = [
		{
			value: 3600,
			label: '1h'
		},
		{
			value: 86400,
			label: '24h'
		},
		{
			value: 604800,
			label: '7d'
		},
		{
			value: 2592000,
			label: '30d'
		},
		{
			value: 31536000,
			label: '1y'
		}
	];

	let timeout: NodeJS.Timeout;

	const setLimitOrderInterval = (newRange: any) => {
		limitOrderInterval = newRange.label;
		limitOrderExpiry.set(newRange.value);
	};

	let priceImpactDeltaInput = getNumBackInUnits($priceImpactDelta, 18).toString();
	let swapSlippageInput = getNumBackInUnits($swapSlippage, 18).toString();
	function onInputChange() {
		priceImpactDeltaInput = sanitizeInputs(priceImpactDeltaInput);
		if (priceImpactDeltaInput) priceImpactDelta.set(toBN(priceImpactDeltaInput).toString());
		else priceImpactDelta.set('0');
	}

	const limitOrderExpiryEventListener = limitOrderExpiry.subscribe(() => {
		let interval = intervals.find((interval) => interval.label == limitOrderInterval);
		if (interval) limitOrderInterval = interval.label;
	});

	async function onSwapInputChange() {
		swapSlippageInput = sanitizeInputs(swapSlippageInput);
		if (swapSlippageInput) swapSlippage.set(toBN(swapSlippageInput).toString());
		else swapSlippage.set('0');

		if ($marginInputForTrade && Object.entries($tokensAllowedForDepositBalance).length != 0) {
			if (timeout) clearTimeout(timeout);
			timeout = setTimeout(
				() =>
					findRequiredCallData(
						$tokensAllowedForDepositBalance,
						new BigNumber($marginInputForTrade)
					),
				300
			);
		}
	}

	onDestroy(() => {
		limitOrderExpiryEventListener();
	});
</script>

{#if showMenu}
	<div
		class="modal relative w-full flex justify-end z-10"
		in:fade={{
			duration: 250,
			easing: quadOut
		}}
	>
		<div on:click={closeMenu} on:keydown={closeMenu} class="w-full fixed h-full top-0 left-0" />

		<div
			class="absolute bg-fx-gradient-8 z-50 transition-right duration-500 right-0 p-4 grid gap-2 rounded-xl squircle-xl mt-4"
		>
			<div class="flex gap-2.5">
				<div class="text-xs text-fx-grey-400 pt-px whitespace-nowrap">
					{$executionType != 'Market' ? 'Order Expiry:' : 'TP/SL Order Expiry'}
				</div>
				<div>
					<div class="flex text-fx-grey-200 text-xs gap-1 rounded-lg squircle-lg w-fit">
						{#each intervals as interval}
							{#if limitOrderInterval == interval.label}
								<GradientBorder
									gradient="bg-fx-gradient-6"
									paddingClass="p-px"
									borderRadius="rounded-md"
									outerClassList=" w-full h-full"
									innerClassList="flex justify-center items-center w-full h-full"
								>
									<div
										class="px-1 py-0.5 cursor-pointer hover:bg-fx-grey-700 rounded-md text-fx-grey-100 text-xs"
									>
										{interval.label}
									</div>
								</GradientBorder>
							{:else}
								<div class="p-px">
									<button
										class="px-1 py-0.5 cursor-pointer hover:bg-fx-grey-700 rounded text-fx-grey-300 text-xs"
										on:click={() => setLimitOrderInterval(interval)}
										on:keydown={() => setLimitOrderInterval(interval)}
									>
										{interval.label}
									</button>
								</div>
							{/if}
						{/each}
					</div>
				</div>
			</div>

			<div class="flex justify-between gap-2">
				<div class="text-xs text-fx-grey-400 pt-1 whitespace-nowrap">Price Impact Delta:</div>
				<div class="flex gap-1">
					<div
						class="text-right bg-transparent border border-fx-grey-500 rounded-lg squircle-lg flex px-2 py-1 text-xs text-fx-grey-200"
					>
						<input
							class="bg-transparent text-fx-grey-200 font-medium outline-none text-xs text-right w-8"
							inputmode="decimal"
							autocomplete="off"
							autocorrect="off"
							pattern="^[0-9]*[.,]?[0-9]*$"
							minlength="1"
							maxlength="5"
							spellcheck="false"
							type="string"
							placeholder="0.05"
							bind:value={priceImpactDeltaInput}
							on:input={onInputChange}
						/>
					</div>
					<div class="text-xs text-fx-grey-400 pt-1 whitespace-nowrap">%</div>
				</div>
			</div>

			{#if $AUTOSWAP_STABLES}
				<div class="flex justify-between gap-2">
					<div class="text-xs text-fx-grey-400 pt-1 whitespace-nowrap">Auto Swap Slippage:</div>
					<div class="flex gap-1">
						<div
							class="text-right bg-transparent border border-fx-grey-500 rounded-lg squircle-lg flex px-2 py-1 text-xs text-fx-grey-200"
						>
							<input
								class="bg-transparent text-fx-grey-200 font-medium outline-none text-xs text-right w-8"
								inputmode="decimal"
								autocomplete="off"
								autocorrect="off"
								pattern="^[0-9]*[.,]?[0-9]*$"
								minlength="1"
								maxlength="5"
								spellcheck="false"
								type="string"
								placeholder="0.05"
								bind:value={swapSlippageInput}
								on:input={onSwapInputChange}
							/>
						</div>
						<div class="text-xs text-fx-grey-400 pt-1 whitespace-nowrap">%</div>
					</div>
				</div>
			{/if}
		</div>
	</div>
{/if}
